import { useState } from 'react';
import { useStore } from '../store';
import type { Page, WarningSeverity, WarningType } from '../types';

const SEVERITY_ORDER: Record<WarningSeverity, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  info: 3,
};

const SEVERITY_STYLES: Record<WarningSeverity, string> = {
  critical: 'bg-red-50 border-red-200',
  high: 'bg-orange-50 border-orange-200',
  medium: 'bg-amber-50 border-amber-200',
  info: 'bg-slate-50 border-slate-200',
};

const SEVERITY_BADGE: Record<WarningSeverity, string> = {
  critical: 'bg-red-600 text-white',
  high: 'bg-orange-500 text-white',
  medium: 'bg-amber-500 text-white',
  info: 'bg-slate-500 text-white',
};

const SEVERITY_LABEL: Record<WarningSeverity, string> = {
  critical: '重大',
  high: '高',
  medium: '中',
  info: '情報',
};

const TYPE_LABELS: Record<WarningType, string> = {
  BROKEN_LINKS: 'リンク切れ多発',
  SERVER_ERRORS: 'サーバーエラー検出',
  TIMEOUT_BURST: 'タイムアウト連続発生',
  REDIRECT_LOOP: 'リダイレクトループ',
  MISSING_TITLE: 'title 欠落',
  MISSING_H1: 'h1 欠落',
  DUPLICATE_CANONICAL: 'canonical 重複',
  MIXED_CONTENT: '混在コンテンツ',
  SLOW_PAGES: '遅いページ',
  ROBOTS_TXT_SKIPPED: 'robots.txt によるスキップ',
};

// 警告タイプごとの「なぜ問題か / どう直すか」のガイダンス。
// 数字や閾値は CLI 側 (warnings.ts) と整合させること
const TYPE_GUIDE: Record<WarningType, { why: string; how: string }> = {
  BROKEN_LINKS: {
    why: '4xx ページが存在するとユーザーは目的の情報に到達できません。SEO 評価も下がります。',
    how: 'リダイレクト設定を入れる、リンク先を修正する、不要ページなら削除して内部リンクを整理。',
  },
  SERVER_ERRORS: {
    why: '5xx はサーバー側の障害。一時的か恒常的かの切り分けが必要です。',
    how: 'サーバーログを確認。再現するなら CMS／アプリケーションのバグ調査を。',
  },
  TIMEOUT_BURST: {
    why: '応答時間が長すぎてクロール失敗。実ユーザーも離脱している可能性。',
    how: 'インフラ／DB クエリ／重い画像の見直し。--rate-limit を下げて再クロールも有効。',
  },
  REDIRECT_LOOP: {
    why: 'リダイレクトが循環してページに到達できません。',
    how: '.htaccess / nginx / CMS のリダイレクト設定を見直してください。',
  },
  MISSING_TITLE: {
    why: 'title はブラウザタブ・検索結果・SNS シェアに使われる最重要要素です。',
    how: 'CMS / テンプレートで全ページに固有の title が出力されるよう修正。',
  },
  MISSING_H1: {
    why: 'h1 はそのページの主題を示します。SEO・スクリーンリーダ双方に影響。',
    how: 'テンプレートに h1 を追加。デザイン上隠したい場合は visually-hidden で。',
  },
  DUPLICATE_CANONICAL: {
    why: '複数ページが同じ canonical を指していると重複コンテンツ扱いされる恐れ。',
    how: '各ページに固有の canonical を設定するか、不要なページは noindex 化。',
  },
  MIXED_CONTENT: {
    why: 'HTTPS ページから http:// リンクがあると鍵マークが外れる、ブラウザがブロックする等。',
    how: 'リンク先を https:// に書き換える。外部リンクは可能な限り https を選ぶ。',
  },
  SLOW_PAGES: {
    why: 'TTFB が遅いページはユーザー体験を損ない、Core Web Vitals 評価も下げます。',
    how: 'サーバー設定／キャッシュ／CDN／DB クエリの見直し。重いページから優先対応。',
  },
  ROBOTS_TXT_SKIPPED: {
    why: 'robots.txt によりクロール対象外となったページがあります。意図通りか確認を。',
    how: '意図的なら問題なし。意図外なら robots.txt の Disallow を見直してください。',
  },
};

// 警告タイプごとに、ページ配列から該当ページを抽出する。
// CLI 側で警告を生成するロジックと整合させる必要があるが、ここでは Viewer 側で再計算
// （Warning の payload に URL 一覧が無いため）
function affectedPages(type: WarningType, pages: Page[]): Page[] {
  switch (type) {
    case 'BROKEN_LINKS':
      return pages.filter((p) => p.status >= 400 && p.status < 500);
    case 'SERVER_ERRORS':
      return pages.filter((p) => p.status >= 500);
    case 'TIMEOUT_BURST':
      return pages.filter((p) => p.error === 'TIMEOUT');
    case 'MISSING_TITLE':
      return pages.filter((p) => !p.title);
    case 'MISSING_H1':
      return pages.filter((p) => !p.headings || p.headings.h1.length === 0);
    case 'SLOW_PAGES':
      return pages.filter((p) => p.performance && p.performance.ttfb > 3000);
    case 'DUPLICATE_CANONICAL': {
      // canonical 別にページを集計し、2 件以上ある canonical を持つページを返す
      const map = new Map<string, Page[]>();
      for (const p of pages) {
        if (!p.canonical) continue;
        const arr = map.get(p.canonical) ?? [];
        arr.push(p);
        map.set(p.canonical, arr);
      }
      const result: Page[] = [];
      for (const arr of map.values()) {
        if (arr.length > 1) result.push(...arr);
      }
      return result;
    }
    case 'MIXED_CONTENT':
      // ページ自身が https で、内部 or 外部リンクに http が含まれるもの
      return pages.filter((p) => {
        if (!p.url.startsWith('https://')) return false;
        const ext = p.externalLinks?.some((l) => l.url.startsWith('http://')) ?? false;
        const intl = p.internalLinks?.some((l) => l.url.startsWith('http://')) ?? false;
        return ext || intl;
      });
    case 'REDIRECT_LOOP':
    case 'ROBOTS_TXT_SKIPPED':
      return [];
  }
}

export function WarningsView() {
  const data = useStore((s) => s.data);
  if (!data) return <></>;
  const warnings = [...data.warnings].sort(
    (a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity],
  );

  if (warnings.length === 0) {
    return (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-8 text-center">
        <div className="text-2xl">✅</div>
        <p className="mt-2 text-sm font-medium text-emerald-800">
          警告は検出されませんでした
        </p>
        <p className="mt-1 text-xs text-emerald-700">
          {data.crawl.stats.checksPerformed} 種類のチェックを実施
        </p>
      </div>
    );
  }

  // severity 別件数
  const counts: Record<WarningSeverity, number> = {
    critical: 0,
    high: 0,
    medium: 0,
    info: 0,
  };
  for (const w of warnings) counts[w.severity]++;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm">
        <span className="font-semibold text-slate-700">{warnings.length} 件の警告</span>
        <span className="text-slate-300">|</span>
        {(['critical', 'high', 'medium', 'info'] as WarningSeverity[])
          .filter((s) => counts[s] > 0)
          .map((s) => (
            <span key={s} className="flex items-center gap-1.5 text-xs">
              <span
                className={`inline-block h-2 w-2 rounded-full ${
                  s === 'critical'
                    ? 'bg-red-600'
                    : s === 'high'
                      ? 'bg-orange-500'
                      : s === 'medium'
                        ? 'bg-amber-500'
                        : 'bg-slate-500'
                }`}
              />
              {SEVERITY_LABEL[s]} {counts[s]} 件
            </span>
          ))}
        <span className="ml-auto text-xs text-slate-500">
          {data.crawl.stats.checksPerformed} 種類のチェックを実施
        </span>
      </div>

      <ul className="space-y-2">
        {warnings.map((w, idx) => (
          <WarningCard
            key={idx}
            type={w.type}
            severity={w.severity}
            count={w.count}
            url={w.url}
            pages={data.pages}
          />
        ))}
      </ul>
    </div>
  );
}

function WarningCard({
  type,
  severity,
  count,
  url,
  pages,
}: {
  type: WarningType;
  severity: WarningSeverity;
  count?: number;
  url?: string;
  pages: Page[];
}) {
  const [expanded, setExpanded] = useState(false);
  const guide = TYPE_GUIDE[type];
  const affected = affectedPages(type, pages);

  return (
    <li className={`overflow-hidden rounded-lg border ${SEVERITY_STYLES[severity]}`}>
      <button
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-baseline gap-3 px-4 py-3 text-left hover:bg-black/5"
      >
        <span
          className={`shrink-0 rounded px-2 py-0.5 text-[10px] font-semibold uppercase ${SEVERITY_BADGE[severity]}`}
        >
          {SEVERITY_LABEL[severity]}
        </span>
        <span className="font-medium text-slate-900">{TYPE_LABELS[type] ?? type}</span>
        <span className="ml-auto flex items-center gap-3 text-xs text-slate-600">
          {count !== undefined && (
            <span className="tabular-nums">
              {count} {countUnit(type)}
            </span>
          )}
          <span className="select-none text-slate-400">{expanded ? '▾' : '▸'}</span>
        </span>
      </button>

      {expanded && (
        <div className="border-t border-black/5 bg-white/60 px-4 py-3 text-xs text-slate-700">
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <div className="text-[10px] font-semibold uppercase text-slate-500">
                なぜ問題か
              </div>
              <p className="mt-1">{guide.why}</p>
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase text-slate-500">
                どう直すか
              </div>
              <p className="mt-1">{guide.how}</p>
            </div>
          </div>

          {url && (
            <div className="mt-3 rounded border border-slate-200 bg-white p-2">
              <div className="text-[10px] font-semibold uppercase text-slate-500">該当 URL</div>
              <div className="mt-0.5 break-all font-mono text-xs">{url}</div>
            </div>
          )}

          {affected.length > 0 && (
            <div className="mt-3">
              <div className="text-[10px] font-semibold uppercase text-slate-500">
                該当ページ ({affected.length} 件)
              </div>
              <ul className="mt-1 max-h-64 space-y-0.5 overflow-y-auto rounded border border-slate-200 bg-white p-2">
                {affected.slice(0, 100).map((p) => (
                  <li key={p.url} className="flex items-baseline gap-2 text-xs">
                    <a
                      href={p.url}
                      target="_blank"
                      rel="noreferrer"
                      className="truncate font-mono text-slate-700 hover:text-slate-900 hover:underline"
                      title={p.url}
                    >
                      {p.url}
                    </a>
                    <span className="ml-auto shrink-0 text-[10px] text-slate-400">
                      {p.error ?? p.status}
                    </span>
                  </li>
                ))}
                {affected.length > 100 && (
                  <li className="px-1 pt-1 text-[10px] text-slate-500">
                    ... 残り {affected.length - 100} 件はテーブルタブで確認
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

function countUnit(type: WarningType): string {
  switch (type) {
    case 'BROKEN_LINKS':
      return 'ページが 4xx';
    case 'SERVER_ERRORS':
      return 'ページが 5xx';
    case 'TIMEOUT_BURST':
      return 'ページがタイムアウト';
    case 'MISSING_TITLE':
      return 'ページに title 無し';
    case 'MISSING_H1':
      return 'ページに h1 無し';
    case 'DUPLICATE_CANONICAL':
      return 'ページが他ページと同じ canonical を指している';
    case 'MIXED_CONTENT':
      return '件の http リンク';
    case 'SLOW_PAGES':
      return 'ページが TTFB 3000ms 超';
    case 'REDIRECT_LOOP':
    case 'ROBOTS_TXT_SKIPPED':
    default:
      return '件';
  }
}
